import React from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import { getArgs } from '../../utils'

const CONTEXT_PREFIX = 'account_casdoor_context_'
const COMPLETED_CONTEXT_PREFIX = 'account_casdoor_completed_'
const CONTEXT_MAX_AGE = 10 * 60 * 1000
const MAX_AUTOMATIC_RECOVERY_ATTEMPTS = 1
const DEFAULT_CASDOOR_SERVER = 'https://auth.pear.link'
const DEFAULT_BRIDGE_SERVER = 'https://nmsapi.webrtc.win'
const DEFAULT_ENABLED_HOSTS = 'account.openfogos.com'
const DEFAULT_CASDOOR_APPLICATION = 'forum'
const DEFAULT_OPENFOGOS_APPLICATION = 'openfogos'
const CANONICAL_ACCOUNT_SERVER = 'https://account.openfogos.com'
const LEGACY_LOGIN_SERVER = 'https://third.pear.link'
const LEGACY_ACCOUNT_HOSTS = [
  'account.webrtc.win',
  'account.pear.link',
  'account.pear.hk',
]
const OPENFOGOS_HANDOFF_SOURCES = [
  'openfogos.com',
  'www.openfogos.com',
  'i.openfogos.com',
  'i2.openfogos.com',
]

const LEGACY_REDIRECT_TARGETS = {
  nms: 'https://nms.webrtc.win/login',
  sms: 'https://sms.webrtc.win/login',
  oss: 'https://oss.webrtc.win/login',
  kuiconnect: 'https://kuiconnect.webrtc.win/login',
  'pearwrt.com': 'https://pearwrt.com/login',
  'www.pearwrt.com': 'https://www.pearwrt.com/login',
}

const getCasdoorStorages = () => {
  const storages = []
  try {
    if (window.sessionStorage) storages.push(window.sessionStorage)
  } catch (error) {}
  try {
    if (window.localStorage && storages.indexOf(window.localStorage) === -1) storages.push(window.localStorage)
  } catch (error) {}
  return storages
}

const removeStoredValue = key => {
  getCasdoorStorages().forEach(storage => {
    try {
      storage.removeItem(key)
    } catch (error) {}
  })
}

const readStoredJSON = key => {
  const storages = getCasdoorStorages()
  for (let i = 0; i < storages.length; i += 1) {
    let raw = null
    try {
      raw = storages[i].getItem(key)
    } catch (error) {}
    if (!raw) continue
    try {
      return JSON.parse(raw)
    } catch (error) {
      try {
        storages[i].removeItem(key)
      } catch (removeError) {}
    }
  }
  return null
}

const writeStoredJSON = (key, value) => {
  const serialized = JSON.stringify(value)
  let stored = false
  getCasdoorStorages().forEach(storage => {
    try {
      storage.setItem(key, serialized)
      stored = true
    } catch (error) {}
  })
  return stored
}

const removeExpiredCasdoorState = () => {
  const now = Date.now()
  getCasdoorStorages().forEach(storage => {
    const keys = []
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i)
        if (key && (key.indexOf(CONTEXT_PREFIX) === 0 || key.indexOf(COMPLETED_CONTEXT_PREFIX) === 0)) keys.push(key)
      }
    } catch (error) {
      return
    }
    keys.forEach(key => {
      let value = null
      try {
        value = JSON.parse(storage.getItem(key))
      } catch (error) {}
      const timestamp = key.indexOf(COMPLETED_CONTEXT_PREFIX) === 0
        ? Number(value && value.completedAt)
        : Number(value && value.createdAt)
      const age = now - timestamp
      if (!Number.isFinite(age) || age < 0 || age > CONTEXT_MAX_AGE) {
        try {
          storage.removeItem(key)
        } catch (error) {}
      }
    })
  })
}

const base64Url = bytes => {
  let value = ''
  bytes.forEach(byte => { value += String.fromCharCode(byte) })
  return window.btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const randomToken = size => {
  const bytes = new Uint8Array(size)
  window.crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

const getEnabledHosts = () => (process.env.REACT_APP_CASDOOR_HOSTS || DEFAULT_ENABLED_HOSTS)
  .split(',')
  .map(host => host.trim().toLowerCase())
  .filter(Boolean)

export const isCasdoorAccountEnabled = () => (
  process.env.REACT_APP_CASDOOR_ENABLED === 'true' &&
  getEnabledHosts().indexOf(window.location.hostname.toLowerCase()) !== -1
)

export const getAccountSignupTarget = (hostname, originalSearch) => {
  const host = (hostname || '').trim().toLowerCase()
  if (host === 'third.pear.link') {
    const args = getArgs(originalSearch || '')
    const params = new URLSearchParams({ source: 'pearwrt.com' })
    if (args.inviter) params.set('inviter', args.inviter)
    if (args.usermine_inviter) params.set('usermine_inviter', args.usermine_inviter)
    return CANONICAL_ACCOUNT_SERVER + '/signup?' + params.toString()
  }
  if (LEGACY_ACCOUNT_HOSTS.indexOf(host) !== -1) {
    return CANONICAL_ACCOUNT_SERVER + '/signup' + (originalSearch || '')
  }
  return ''
}

export const getLegacyLoginTarget = (hostname, originalSearch) => {
  const host = (hostname || '').trim().toLowerCase()
  if (host === 'account.openfogos.com' || LEGACY_ACCOUNT_HOSTS.indexOf(host) !== -1) {
    return LEGACY_LOGIN_SERVER + '/login' + (originalSearch || '')
  }
  return ''
}

const getRedirectUri = () => process.env.REACT_APP_CASDOOR_REDIRECT_URI || `${window.location.origin}/callback`

export const getLegacyFallbackTarget = context => '/legacy' + ((context && context.originalSearch) || '')

export const getCasdoorFlowConfig = (originalSearch, mode = 'login') => {
  if (getHandoffClient(originalSearch, mode) === 'openfogos') {
    return {
      application: process.env.REACT_APP_CASDOOR_OPENFOGOS_APPLICATION || DEFAULT_OPENFOGOS_APPLICATION,
      clientId: process.env.REACT_APP_CASDOOR_OPENFOGOS_CLIENT_ID || '',
    }
  }
  return {
    application: process.env.REACT_APP_CASDOOR_APPLICATION || DEFAULT_CASDOOR_APPLICATION,
    clientId: process.env.REACT_APP_CASDOOR_CLIENT_ID || '',
  }
}

const getCasdoorRecoveryHint = (originalSearch, mode, recoveryAttempt) => {
  if (mode !== 'login') return ''
  const source = getLegacySource(originalSearch, mode).trim().toLowerCase()
  const sourceIndex = OPENFOGOS_HANDOFF_SOURCES.indexOf(source)
  if (sourceIndex === -1) return ''
  const attempt = Number(recoveryAttempt) >= MAX_AUTOMATIC_RECOVERY_ATTEMPTS ? 1 : 0
  return `o${sourceIndex}${attempt}`
}

export const getCasdoorRecoveryContext = state => {
  const match = String(state || '').match(/\.o([0-3])([01])$/)
  if (!match) return null
  const source = OPENFOGOS_HANDOFF_SOURCES[Number(match[1])]
  const attempted = Number(match[2])
  if (!source) return null
  return {
    mode: 'login',
    originalSearch: '?' + new URLSearchParams({ source }).toString(),
    recoveryAttempt: MAX_AUTOMATIC_RECOVERY_ATTEMPTS,
    autoRetry: attempted < MAX_AUTOMATIC_RECOVERY_ATTEMPTS,
  }
}

export const startCasdoorAccountFlow = async (mode, originalSearch, recoveryAttempt = 0) => {
  const flow = getCasdoorFlowConfig(originalSearch, mode)
  const clientId = flow.clientId
  if (!clientId) {
    throw new Error('Casdoor client ID is not configured')
  }

  const verifier = randomToken(48)
  const recoveryHint = getCasdoorRecoveryHint(originalSearch, mode, recoveryAttempt)
  const state = randomToken(24) + (recoveryHint ? '.' + recoveryHint : '')
  const nonce = randomToken(24)
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  const path = mode === 'signup' ? '/signup/oauth/authorize' : '/login/oauth/authorize'
  const url = new URL(path, process.env.REACT_APP_CASDOOR_SERVER || DEFAULT_CASDOOR_SERVER)
  removeExpiredCasdoorState()
  if (!writeStoredJSON(CONTEXT_PREFIX + state, {
    verifier,
    mode: mode === 'signup' ? 'signup' : 'login',
    originalSearch: originalSearch || '',
    clientId,
    application: flow.application,
    recoveryAttempt,
    createdAt: Date.now(),
  })) {
    throw new Error('Browser storage is unavailable')
  }

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: getRedirectUri(),
    scope: 'openid profile email phone',
    state,
    nonce,
    code_challenge_method: 'S256',
    code_challenge: base64Url(new Uint8Array(digest)),
  })
  url.search = params.toString()
  window.location.assign(url.toString())
}

export const consumeCasdoorContext = query => {
  const state = query.get('state')
  const code = query.get('code')
  if (!state) {
    throw new Error('Casdoor callback is incomplete')
  }
  const key = CONTEXT_PREFIX + state
  const context = readStoredJSON(key)
  if (!context) {
    const completedKey = COMPLETED_CONTEXT_PREFIX + state
    const completed = readStoredJSON(completedKey)
    if (completed) {
      const age = Date.now() - Number(completed.completedAt)
      if (typeof completed.target === 'string' && completed.target && Number.isFinite(age) && age >= 0 && age <= CONTEXT_MAX_AGE) {
        return { completedTarget: completed.target }
      }
      removeStoredValue(completedKey)
    }
    const error = new Error('Casdoor callback state is invalid')
    const recoveryContext = getCasdoorRecoveryContext(state)
    if (recoveryContext) {
      error.retryContext = recoveryContext
      error.autoRetry = recoveryContext.autoRetry
    }
    throw error
  }
  if (!code) {
    const error = new Error(query.get('error_description') || query.get('error') || 'Casdoor callback is incomplete')
    error.retryContext = context
    throw error
  }
  const age = Date.now() - Number(context.createdAt)
  if (typeof context.verifier !== 'string' || !context.verifier || !Number.isFinite(age) || age < 0 || age > CONTEXT_MAX_AGE) {
    removeStoredValue(key)
    const error = new Error('Casdoor callback state has expired')
    error.retryContext = context
    throw error
  }
  return { code, context, state }
}

export const rememberCompletedCasdoorContext = (state, target) => {
  if (!state || !target) return
  const completedTarget = target.replace(/#handoff=[^#]*$/, '')
  if (writeStoredJSON(COMPLETED_CONTEXT_PREFIX + state, { target: completedTarget, completedAt: Date.now() })) {
    removeStoredValue(CONTEXT_PREFIX + state)
  }
}

export const getCasdoorExchangePayload = (code, verifier, originalSearch, mode = 'login', clientId = '') => {
  const args = getArgs(originalSearch || '')
  const flow = getCasdoorFlowConfig(originalSearch, mode)
  const payload = {
    code,
    code_verifier: verifier,
    client_id: clientId || flow.clientId,
    inviter: args.inviter || '',
    usermine_inviter: args.usermine_inviter || '',
  }
  const handoffClient = getHandoffClient(originalSearch, mode)
  if (handoffClient) {
    payload.handoff_client = handoffClient
  }
  return payload
}

const exchangeCasdoorCode = async (code, verifier, originalSearch, mode, clientId) => {
  const response = await fetch((process.env.REACT_APP_PERMISSION_SERVER || DEFAULT_BRIDGE_SERVER) + '/account/oauth2/casdoor', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(getCasdoorExchangePayload(code, verifier, originalSearch, mode, clientId)),
  })
  const result = await response.json()
  const data = result && (result.data || result)
  if (!response.ok || !data || !data.access_token || !data.token || !data.user_id || !data.username) {
    throw new Error((result && result.msg) || 'Casdoor token exchange failed')
  }
  return data
}

const legacyResultQuery = result => {
  const legacyResult = {
    user_id: result.user_id,
    username: result.username,
    token: result.token,
  }
  return encodeURIComponent(Object.entries(legacyResult).reduce((query, item) => `${query}&${item[0]}=${item[1]}`, '').slice(1))
}

export const getLegacySource = (originalSearch, mode) => {
  const args = getArgs(originalSearch || '')
  if (mode === 'signup') {
    return args.source || 'nms'
  }
  return args.state === 'public' ? '' : (args.state || args.source || '')
}

export const getHandoffClient = (originalSearch, mode) => {
  const source = getLegacySource(originalSearch, mode).trim().toLowerCase()
  return OPENFOGOS_HANDOFF_SOURCES.indexOf(source) !== -1 ? 'openfogos' : ''
}

export const getLegacyRedirectTarget = (source, query, referrer) => {
  const normalizedSource = String(source || '').trim().toLowerCase()
  const redirectURL = LEGACY_REDIRECT_TARGETS[normalizedSource]
  if (!redirectURL) {
    return '/legacy?' + query
  }
  if (normalizedSource === 'kuiconnect') {
    query = 'ip%3D' + encodeURIComponent(referrer || '') + '%26' + query
  }
  return redirectURL + '?' + query
}

export const getCasdoorRedirectTarget = (source, query, referrer, result, originalSearch, mode) => {
  if (getHandoffClient(originalSearch, mode) === 'openfogos') {
    if (!result || !result.handoff_code) {
      throw new Error('OpenFogOS login handoff is unavailable')
    }
    const trustedSource = getLegacySource(originalSearch, mode).trim().toLowerCase()
    return 'https://' + trustedSource + '/login#handoff=' + encodeURIComponent(result.handoff_code)
  }
  return getLegacyRedirectTarget(source, query, referrer)
}

export const redirectWithLegacyContract = (result, originalSearch, mode, state) => {
  const source = getLegacySource(originalSearch, mode)
  if (getHandoffClient(originalSearch, mode) === 'openfogos') {
    const target = getCasdoorRedirectTarget(source, '', document.referrer, result, originalSearch, mode)
    rememberCompletedCasdoorContext(state, target)
    window.location.href = target
    return
  }

  const query = legacyResultQuery(result)

  localStorage.setItem('token', result.token)
  localStorage.setItem('access_token', result.access_token)
  localStorage.setItem('user_id', result.user_id)
  localStorage.setItem('redirectQuery', '/login?' + query)

  if (!source) {
    const target = '/legacy?' + query
    rememberCompletedCasdoorContext(state, target)
    window.location.replace(target)
    return
  }
  const target = getCasdoorRedirectTarget(source, query, document.referrer, result, originalSearch, mode)
  rememberCompletedCasdoorContext(state, target)
  window.location.href = target
}

class CasdoorRedirect extends React.Component {
  state = { error: '' }

  componentDidMount () {
    startCasdoorAccountFlow(this.props.mode, this.props.location.search)
      .catch(error => this.setState({ error: error.message }))
  }

  render () {
    if (this.state.error) {
      return <div style={{ textAlign: 'center', marginTop: 80 }}>
        <p>{this.state.error}</p>
        <Button variant="outlined" color="primary" onClick={() => window.location.assign('/legacy' + this.props.location.search)}>旧版登录入口</Button>
      </div>
    }
    return <div style={{ textAlign: 'center', marginTop: 96 }}><CircularProgress /></div>
  }
}

export class CasdoorCallback extends React.Component {
  state = { error: '', retryContext: null }

  componentDidMount () {
    Promise.resolve()
      .then(() => consumeCasdoorContext(new URLSearchParams(this.props.location.search)))
      .then(({ code, context, state, completedTarget }) => {
        if (completedTarget) {
          window.location.replace(completedTarget)
          return null
        }
        this.retryContext = context
        return exchangeCasdoorCode(code, context.verifier, context.originalSearch, context.mode, context.clientId)
          .then(result => redirectWithLegacyContract(result, context.originalSearch, context.mode, state))
      })
      .catch(error => {
        const retryContext = error.retryContext || this.retryContext
        if (error.autoRetry && retryContext) {
          this.retryContext = retryContext
          return startCasdoorAccountFlow(retryContext.mode, retryContext.originalSearch, retryContext.recoveryAttempt)
            .catch(retryError => this.setState({ error: retryError.message, retryContext }))
        }
        this.setState({ error: error.message, retryContext })
      })
  }

  retry = () => {
    const context = this.state.retryContext
    startCasdoorAccountFlow(
      context ? context.mode : 'login',
      context ? context.originalSearch : '',
      context ? context.recoveryAttempt : 0
    )
      .catch(error => this.setState({ error: error.message }))
  }

  useLegacyLogin = () => window.location.assign(getLegacyFallbackTarget(this.state.retryContext))

  render () {
    if (this.state.error) {
      return <div style={{ textAlign: 'center', marginTop: 80 }}>
        <p>{this.state.error}</p>
        <Button variant="contained" onClick={this.retry}>重新登录</Button>
        <Button variant="outlined" color="primary" onClick={this.useLegacyLogin} style={{ marginLeft: 8 }}>旧版登录入口</Button>
      </div>
    }
    return <div style={{ textAlign: 'center', marginTop: 96 }}><CircularProgress /></div>
  }
}

export class CasdoorPasswordRedirect extends React.Component {
  componentDidMount () {
    const server = process.env.REACT_APP_CASDOOR_SERVER || DEFAULT_CASDOOR_SERVER
    const application = getCasdoorFlowConfig(this.props.location.search, this.props.mode).application
    const path = this.props.mode === 'forget' ? `/forget/${encodeURIComponent(application)}` : '/account'
    window.location.replace(new URL(path, server).toString())
  }

  render () {
    return <div style={{ textAlign: 'center', marginTop: 96 }}><CircularProgress /></div>
  }
}

export default CasdoorRedirect
